import { AssetCache } from './cache.js';
import { assetUrl } from './asset-paths.js';
import { createViewerWorker } from './viewer-worker.js';

/** The same download identities and parser worker used by Live and Explorer. */
export class AssetSession {
    constructor({ diagnostic = () => {}, revisionChanged = () => {}, cache = new AssetCache(diagnostic), workerFactory = () => createViewerWorker('worker') } = {}) {
        Object.assign(this, { diagnostic, revisionChanged, cache, workerFactory }); this.generation = 0; this.sequence = 0; this.pending = new Map();
    }
    close() {
        this.generation++; this.abort?.abort(); this.worker?.terminate(); this.worker = null;
        for (const p of this.pending.values()) p.reject(new DOMException('Cancelled', 'AbortError'));
        this.pending.clear(); this.cache.active.clear();
    }
    async open(assetSet, mapName = null, progress = () => {}) {
        this.close(); const generation = this.generation;
        this.failure = null; this.abort = new AbortController(); const signal = this.abort.signal;
        let bytes; this.rootUrl = null;
        if (mapName) {
            const response = await fetch(`/maps/${encodeURIComponent(mapName)}`, { signal, cache: 'no-cache' });
            if (!response.ok) throw new Error(`Map unavailable (${response.status})`);
            const map = await response.json(); assetSet = map.assetSet;
            this.rootUrl = map.bspUrl;
            bytes = await this.cache.fetch(map.bspUrl, signal, (size, total) => progress(`Downloading BSP · ${(size / 1048576).toFixed(1)} / ${(total / 1048576).toFixed(1)} MiB`));
        }
        signal.throwIfAborted(); this.assetSet = assetSet; this.mapName = mapName;
        const worker = this.worker = this.workerFactory();
        const fail = error => { if (generation !== this.generation) return; for (const p of this.pending.values()) p.reject(error); this.pending.clear(); this.failure = error; worker.terminate(); this.diagnostic(error.message); };
        worker.onerror = () => fail(new Error('Asset parser failed. Retry to reopen the root.'));
        worker.onmessageerror = () => fail(new Error('Asset parser response could not be read.'));
        worker.onmessage = async ({ data }) => {
            if (generation !== this.generation || data.loadId !== generation) return;
            if (data.type === 'diagnostic') { this.diagnostic(data.message); return; }
            if (data.type === 'asset' || data.type === 'particle-index') {
                try {
                    const url = data.type === 'asset' ? assetUrl(data.path, assetSet)
                        : `/particles?assetSet=${encodeURIComponent(assetSet)}${mapName ? `&map=${encodeURIComponent(mapName)}` : ''}`;
                    const bytes = await this.cache.fetch(url, signal);
                    if (generation === this.generation) worker.postMessage({ type: 'asset', requestId: data.requestId, bytes: bytes.buffer }, [bytes.buffer]);
                } catch (error) {
                    if (generation === this.generation) { worker.postMessage({ type: 'asset', requestId: data.requestId, error: error.message }); if(error.message === 'asset_revision_changed') this.revisionChanged(); }
                }
                return;
            }
            const pending = this.pending.get(data.requestId); this.pending.delete(data.requestId);
            if (data.type === 'error') pending?.reject(new Error(data.message)); else pending?.resolve(data);
        };
        progress(mapName ? 'Indexing embedded assets…' : 'Opening filesystem…');
        return this.rpc('asset-session', { mapName, bytes: bytes?.buffer }, bytes ? [bytes.buffer] : []);
    }
    activatePreview() { this.cache.active.clear(); if(this.rootUrl) this.cache.active.add(this.rootUrl); }
    rpc(type, data = {}, transfer = []) {
        if (this.failure) return Promise.reject(this.failure);
        if (!this.worker || this.abort?.signal.aborted) return Promise.reject(new DOMException('Closed asset session', 'AbortError'));
        const requestId = ++this.sequence;
        return new Promise((resolve, reject) => {
            this.pending.set(requestId, { resolve, reject });
            try { this.worker.postMessage({ type, loadId: this.generation, requestId, ...data }, transfer); }
            catch (error) { this.pending.delete(requestId); reject(error); }
        });
    }
}
